import { useState } from 'react';

import { formatCurrency } from '../utilities/general-utils';

// Constants for FICA
const FICA_SOCIAL_SECURITY_RATE = 0.062;
const FICA_MEDICARE_RATE = 0.0145;
const FICA_ADDITIONAL_MEDICARE_RATE = 0.009;
const FICA_ADDITIONAL_MEDICARE_THRESHOLD = 200000; // For single filers (adjust for other statuses)

// Standard Deductions for 2026
const STANDARD_DEDUCTION = {
    'single': 16150,
    'married_joint': 32300,
    'married_separate': 16150,
    'head_of_household': 24250
};

const STANDARD_DEDUCTION_IDAHO = {
    'single': 15400,
    'married_joint': 30800,
    'married_separate': 15400,
    'head_of_household': 23100
};

// Federal Tax Brackets for 2026
const FEDERAL_TAX_BRACKETS = {
    'single': [
        [12400, 0.10],
        [50400, 0.12],
        [105700, 0.22],
        [201775, 0.24],
        [256225, 0.32],
        [640600, 0.35],
        [Infinity, 0.37]
    ],
    'married_joint': [
        [24800, 0.10],
        [100800, 0.12],
        [211400, 0.22],
        [403550, 0.24],
        [512450, 0.32],
        [768700, 0.35],
        [Infinity, 0.37]
    ],
    'married_separate': [
        [12400, 0.10],
        [50400, 0.12],
        [105700, 0.22],
        [201775, 0.24],
        [256225, 0.32],
        [384350, 0.35],
        [Infinity, 0.37]
    ],
    'head_of_household': [
        [17700, 0.10],
        [67450, 0.12],
        [105700, 0.22],
        [201750, 0.24],
        [256200, 0.32],
        [640600, 0.35],
        [Infinity, 0.37]
    ]
};

// Child Tax Credit for 2026
const CHILD_TAX_CREDIT = 2300;
const CHILD_TAX_CREDIT_REFUNDABLE = 1700;
const PHASE_OUT_START = {
    'single': 200000,
    'married_joint': 400000,
    'married_separate': 200000,
    'head_of_household': 200000
};

const CHILD_TAX_CREDIT_STATE = 155;

const PaycheckCalculator = () => {
    const [grossIncome, setGrossIncome] = useState(5451.08);
    const [filingStatus, setFilingStatus] = useState('married_joint');
    const [paymentFrequency, setPaymentFrequency] = useState('biweekly');
    const [stateTaxRate, setStateTaxRate] = useState(0.058);
    const [retirementContribution, setRetirementContribution] = useState(0);
    const [retirementContributionType, setRetirementContributionType] = useState('percentage');
    const [healthInsurancePremium, setHealthInsurancePremium] = useState(169.76);
    const [otherPreTax, setOtherPreTax] = useState(150.76);
    const [postTax, setPostTax] = useState(48);
    const [numQualifyingChildren, setNumQualifyingChildren] = useState(5);
    const [paycheckDetails, setPaycheckDetails] = useState(null);

    const handleContributionTypeChange = (e) => {
        if (e.target.checked) {
            setRetirementContributionType(e.target.value);
            setRetirementContribution(0);
        }
    }

    const handleSubmit = (e) => {
        e.preventDefault();

        const paycheckDetails = calculatePaycheck(
            grossIncome,
            paymentFrequency,
            filingStatus,
            stateTaxRate,
            retirementContribution,
            retirementContributionType,
            healthInsurancePremium,
            otherPreTax,
            postTax,
            numQualifyingChildren,
        );
        setPaycheckDetails(paycheckDetails);
    }

    return (
        <div className="container">
            <h1 className="text-center">Paycheck Calculator</h1>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    flexWrap: 'wrap',
                    alignItems: 'flex-start',
                    margin: '0 auto'
                }}
            >
                <div className="standardFormWrap" style={{margin: '0 1rem 1rem 0'}}>
                    <form onSubmit={handleSubmit} className="formStandard" noValidate>
                        <div className="formField">
                            <label>Gross Income</label>
                            <input type="number" className="form-control" value={grossIncome} onChange={(e) => setGrossIncome(e.target.value)} />
                        </div>
                        <div className="formField">
                            <label>Filing Status</label>
                            <select className="form-control" value={filingStatus} onChange={(e) => setFilingStatus(e.target.value)}>
                                <option value="single">Single</option>
                                <option value="married_separate">Married (Separate)</option>
                                <option value="married_joint">Married (Joint)</option>
                                <option value="head_of_household">Head of Household</option>
                            </select>
                        </div>
                        <div className="formField">
                            <label>Payment Frequency</label>
                            <select className="form-control" value={paymentFrequency} onChange={(e) => setPaymentFrequency(e.target.value)}>
                                <option value="weekly">Weekly</option>
                                <option value="biweekly">Biweekly</option>
                                <option value="monthly">Monthly</option>
                                <option value="annually">Annually</option>
                            </select>
                        </div>
                        <div className="formField">
                            <label>State Tax Rate</label>
                            <input type="number" className="form-control" value={stateTaxRate} onChange={(e) => setStateTaxRate(e.target.value)} />
                        </div>

                        <h4>
                            Deductions
                        </h4>

                        <div className="formField">
                            <label>Retirement Contribution</label>
                            <div>
                                <label>
                                    <input
                                        defaultChecked={retirementContributionType === 'percentage'}
                                        onChange={handleContributionTypeChange}
                                        type="radio"
                                        name="retirement-contribution-type"
                                        value="percentage"
                                    />
                                    % of Paycheck
                                </label>
                                <label>
                                    <input
                                        defaultChecked={retirementContributionType === 'fixed'}
                                        onChange={handleContributionTypeChange}
                                        type="radio"
                                        name="retirement-contribution-type"
                                        value="fixed"
                                    />
                                    Fixed Amount
                                </label>
                            </div>
                            <input type="number" className="form-control" value={retirementContribution} onChange={(e) => setRetirementContribution(e.target.value)} />
                        </div>
                        <div className="formField">
                            <label>Health Insurance</label>
                            <input type="number" className="form-control" value={healthInsurancePremium} onChange={(e) => setHealthInsurancePremium(e.target.value)} />
                        </div>
                        <div className="formField">
                            <label>Pre-Tax Deductions (Other)</label>
                            <input type="number" className="form-control" value={otherPreTax} onChange={(e) => setOtherPreTax(e.target.value)} />
                        </div>
                        <div className="formField">
                            <label>Post-Tax Deductions</label>
                            <input type="number" className="form-control" value={postTax} onChange={(e) => setPostTax(e.target.value)} />
                        </div>
                        <div className="formField">
                            <label>Number of Qualifying Children</label>
                            <input type="number" className="form-control" value={numQualifyingChildren} onChange={(e) => setNumQualifyingChildren(e.target.value)} />
                        </div>

                        <button type="submit" className="button button-green spc-m-t-1">Calculate Paycheck</button>
                    </form>
                </div>
                
                {paycheckDetails &&
                    <div style={{
                        padding: '0 2rem 2rem',
                        border: '1px solid #d1d5db',
                        backgroundColor: '#fff',
                    }}>
                        <div>
                            <h4>Income: </h4>
                            <table className="table-default" style={{width: '100%'}}>
                                <tbody>
                                    <tr>
                                        <th className="text-left">Gross:</th>
                                        <td>{formatCurrency(paycheckDetails.grossIncome)}</td>
                                    </tr>
                                    <tr>
                                        <th className="text-left">Net:</th>
                                        <td>{formatCurrency(paycheckDetails.netIncome)}</td>
                                    </tr>
                                    <tr>
                                        <th className="text-left">Taxable:</th>
                                        <td>{formatCurrency(paycheckDetails.taxableIncome)}</td>
                                    </tr>
                                </tbody>
                            </table>

                            <h4 className={'spc-m-t-2'}>
                                Taxes:
                            </h4>
                            <table className="table-default" style={{width: '100%'}}>
                                <tbody>
                                    <tr>
                                        <th className="text-left">Social Security:</th>
                                        <td>{formatCurrency(paycheckDetails.socialSecurity)}</td>
                                    </tr>
                                    <tr>
                                        <th className="text-left">Medicare:</th>
                                        <td>{formatCurrency(paycheckDetails.medicare)}</td>
                                    </tr>
                                    <tr>
                                        <th className="text-left">Federal:</th>
                                        <td>{formatCurrency(paycheckDetails.federalTax)}</td>
                                    </tr>
                                    <tr>
                                        <th className="text-left">State:</th>
                                        <td>{formatCurrency(paycheckDetails.stateTax)}</td>
                                    </tr>
                                </tbody>
                            </table>

                            <h4 className={'spc-m-t-2'}>
                                Deductions:
                            </h4>
                            <table className="table-default" style={{width: '100%'}}>
                                <tbody>
                                    <tr>
                                        <th className="text-left">Retirement:</th>
                                        <td>{formatCurrency(paycheckDetails.retirementContribution)}</td>
                                    </tr>
                                    <tr>
                                        <th className="text-left">Health Insurance:</th>
                                        <td>{formatCurrency(paycheckDetails.healthInsurancePremium)}</td>
                                    </tr>
                                    <tr>
                                        <th className="text-left">Other Pre-Tax:</th>
                                        <td>{formatCurrency(paycheckDetails.otherPreTax)}</td>
                                    </tr>
                                    <tr>
                                        <th className="text-left">Post-Tax:</th>
                                        <td>{formatCurrency(paycheckDetails.postTax)}</td>
                                    </tr>
                                    <tr>
                                        <th className="text-left">Refundable Child Tax Credit:</th>
                                        <td>{formatCurrency(paycheckDetails.refundableChildTaxCredit)}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                }
            </div>
        </div>
    );
}

export default PaycheckCalculator;

function calculatePaycheck(
    grossIncomeParam,
    paymentFrequencyParam = 'biweekly',
    filingStatusParam,
    stateTaxRateParam,
    retirementContributionParam = 0,
    retirementContributionType,
    healthInsurancePremiumParam = 0,
    otherPreTaxParam = 0,
    postTaxParam = 0,
    numQualifyingChildrenParam = 0,
) {
    let multiplier = 26;
    let paymentFrequency = paymentFrequencyParam;
 
    switch (paymentFrequency) {
        case 'weekly':
            multiplier = 52;
            break;
        case 'biweekly':
            multiplier = 26;
            break;
        case 'monthly':
            multiplier = 12;
            break;
        case 'annually':
            multiplier = 1;
            break;
        default:
            multiplier = 26;
    }

    let grossIncome = parseFloat(grossIncomeParam) * multiplier;
    let filingStatus = filingStatusParam;
    let stateTaxRate = parseFloat(stateTaxRateParam);
    let retirementContributionRate = parseFloat(retirementContributionParam);
    let healthInsurancePremium = parseFloat(healthInsurancePremiumParam * multiplier);
    let otherPreTax = parseFloat(otherPreTaxParam * multiplier);
    let postTax = parseFloat(postTaxParam * multiplier);
    let numQualifyingChildren = parseInt(numQualifyingChildrenParam);

    // FICA Calculation
    let socialSecurity = grossIncome * FICA_SOCIAL_SECURITY_RATE;
    let medicare = grossIncome * FICA_MEDICARE_RATE;

    // Additional Medicare Tax
    let additionalMedicare = 0;
    if (grossIncome > FICA_ADDITIONAL_MEDICARE_THRESHOLD) {
        additionalMedicare = (grossIncome - FICA_ADDITIONAL_MEDICARE_THRESHOLD) * FICA_ADDITIONAL_MEDICARE_RATE;
    }

    let totalFica = socialSecurity + medicare + additionalMedicare;
    let retirementDeduction = retirementContributionType === 'percentage'
        ? grossIncome * (retirementContributionRate / 100)
        : retirementContributionParam * multiplier;

    // Federal Income Tax Calculation
    let annualIncome = grossIncome;
    let taxableIncome = annualIncome - (STANDARD_DEDUCTION[filingStatus] + retirementDeduction + healthInsurancePremium + otherPreTax);
    let federalTaxBeforeCredits = calculateFederalTax(taxableIncome, filingStatus);

    // Apply child tax credit (simplified — only non-refundable part)
    let totalCTC = Math.min(numQualifyingChildren * CHILD_TAX_CREDIT, federalTaxBeforeCredits);
    let federalTaxAfterCredits = Math.max(0, federalTaxBeforeCredits - totalCTC);

    // State Income Tax Calculation
    let stateDeduction = STANDARD_DEDUCTION_IDAHO[filingStatus];
    let stateChildTaxCredit = numQualifyingChildren * CHILD_TAX_CREDIT_STATE;
    let stateTaxableIncome = annualIncome - (stateDeduction + retirementDeduction + healthInsurancePremium + otherPreTax);
    let stateTax = Math.max(0, (stateTaxableIncome - stateChildTaxCredit) * stateTaxRate);

    // Child Tax Credit Calculation (for refundable portion)
    let totalChildTaxCredit = numQualifyingChildren * CHILD_TAX_CREDIT;
    let phaseOutThreshold = PHASE_OUT_START[filingStatus];
    let phaseOutAmount = 0;

    if (annualIncome > phaseOutThreshold) {
        phaseOutAmount = Math.ceil((annualIncome - phaseOutThreshold) / 1000) * 50; // $50 per $1000 over threshold
        totalChildTaxCredit = Math.max(0, totalChildTaxCredit - phaseOutAmount);
    }

    let nonRefundableCredit = Math.min(federalTaxBeforeCredits, totalChildTaxCredit);
    let refundableCredit = Math.min(
        Math.max(0, totalChildTaxCredit - nonRefundableCredit),
        numQualifyingChildren * CHILD_TAX_CREDIT_REFUNDABLE
    );

    // Calculate Net Income (add refundable credits)
    let netIncome = grossIncome - (totalFica + federalTaxAfterCredits + stateTax + retirementDeduction + healthInsurancePremium + otherPreTax + postTax) + refundableCredit;

    return {
        grossIncome: grossIncome / multiplier,
        taxableIncome: taxableIncome / multiplier,
        fica: totalFica / multiplier,
        socialSecurity: socialSecurity / multiplier,
        medicare: (medicare + additionalMedicare) / multiplier,
        federalTax: federalTaxAfterCredits / multiplier,
        stateTax: stateTax / multiplier,
        retirementContribution: retirementDeduction / multiplier,
        healthInsurancePremium: healthInsurancePremium / multiplier,
        otherPreTax: otherPreTax / multiplier,
        postTax: postTax / multiplier,
        refundableChildTaxCredit: refundableCredit / multiplier,
        netIncome: netIncome / multiplier
    };
}

const calculateFederalTax = (taxableIncome, filingStatus) => {
    if (taxableIncome <= 0) return 0;
    
    const brackets = FEDERAL_TAX_BRACKETS[filingStatus];
    let taxOwed = 0;
    let lastLimit = 0;

    for (let i = 0; i < brackets.length; i++) {
        const [limit, rate] = brackets[i];
        if (taxableIncome > limit) {
            taxOwed += (limit - lastLimit) * rate;
            lastLimit = limit;
        } else {
            taxOwed += (taxableIncome - lastLimit) * rate;
            break;
        }
    }

    return Math.max(0, taxOwed);
};
